import { useEffect, useState } from "react";
import { Inbox, Loader2 } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";
import { Button } from "@/components/ui/button";
import { SameTeamBadge } from "@/components/ui/same-team-badge";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface QueryRow {
  id: number;
  patientName: string | null;
  requestType: string;
  status: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  assignedToTeamCode: string | null;
  detail: string | null;
  createdAt: string;
}

interface TeamMember {
  userId: string;
  displayName: string | null;
  email: string | null;
  role: string | null;
  teamCode: string | null;
}

export default function CoordinatorQueries() {
  const [rows, setRows] = useState<QueryRow[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [viewerTeamCode, setViewerTeamCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/api/coordinator/queries`, { credentials: "include" }).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      ),
      fetch(`${BASE}/api/coordinator/team`, { credentials: "include" }).then((r) =>
        r.ok ? r.json() : [],
      ),
      fetch(`${BASE}/api/user/profile-summary`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([q, t, me]) => {
        setRows(q as QueryRow[]);
        const clinicalRoles = ["nurse", "doctor", "pharmacist", "physio", "ot"];
        setTeam(
          (t as TeamMember[]).filter((m) => m.role && clinicalRoles.includes(m.role)),
        );
        setViewerTeamCode((me as { teamCode?: string | null } | null)?.teamCode ?? null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const route = async (id: number, assignedToUserId: string) => {
    if (!assignedToUserId) return;
    setBusy(id);
    try {
      const res = await fetch(`${BASE}/api/coordinator/requests/${id}/route`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ assignedToUserId, status: "assigned" }),
      });
      if (res.ok) load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <Inbox className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Advice query queue</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Prescription queries and blood form requests. Route each item to a
          clinician — you cannot reply to clinical content yourself.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center">
            Advice queue is empty.
          </p>
        )}

        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className="bg-white p-4 rounded-2xl border border-slate-200"
              data-testid={`query-row-${r.id}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <p className="font-semibold text-slate-900">
                  {r.patientName ?? "Unknown patient"}
                </p>
                <span className="text-xs text-slate-500 px-2 py-0.5 rounded-full bg-slate-100">
                  {r.requestType}
                </span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {r.status ?? "pending"}
                </span>
              </div>
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-2 flex-wrap">
                <span>Submitted {new Date(r.createdAt).toLocaleString("en-GB")}</span>
                {r.assignedToUserId && (
                  <span>
                    · assigned to{" "}
                    <span className="text-slate-700">
                      {r.assignedToName ?? r.assignedToUserId}
                    </span>
                    {r.assignedToTeamCode ? ` (Team ${r.assignedToTeamCode})` : ""}
                  </span>
                )}
                {r.assignedToTeamCode && (
                  <SameTeamBadge
                    viewerTeamCode={viewerTeamCode}
                    otherTeamCode={r.assignedToTeamCode}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                  defaultValue=""
                  onChange={(e) => route(r.id, e.target.value)}
                  disabled={busy === r.id || team.length === 0}
                  data-testid={`select-route-${r.id}`}
                >
                  <option value="" disabled>
                    {team.length === 0
                      ? "No clinicians available"
                      : "Route to clinician…"}
                  </option>
                  {team.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.displayName ?? m.email ?? m.userId} ({m.role})
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === r.id}
                  onClick={async () => {
                    setBusy(r.id);
                    await fetch(`${BASE}/api/coordinator/requests/${r.id}/status`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ status: "resolved" }),
                    });
                    setBusy(null);
                    load();
                  }}
                >
                  Mark resolved
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CoordinatorLayout>
  );
}
